import React, { useState } from "react";
import { registerUser, loginUser, forgotPasswordUser } from "../api/authApi";
import PasswordInput from "./PasswordInput";

const emptyRegisterForm = {
  firstName: "",
  lastName: "",
  mobile: "",
  email: "",
  username: "",
  password: "",
  confirmPassword: "",
};

const emptyLoginForm = {
  username: "",
  password: "",
};

const generateCaptcha = () => {
  const firstNumber = Math.floor(Math.random() * 9) + 1;
  const secondNumber = Math.floor(Math.random() * 9) + 1;

  return {
    question: `${firstNumber} + ${secondNumber} = ?`,
    answer: firstNumber + secondNumber,
  };
};

const normalizeRegisterForm = (form) => ({
  firstName: form.firstName.trim(),
  lastName: form.lastName.trim(),
  mobile: form.mobile.trim(),
  email: form.email.trim().toLowerCase(),
  username: form.username.trim(),
  password: form.password,
  confirmPassword: form.confirmPassword,
});

function AuthModal({ mode = "login", onClose, onLogin }) {
  const [activeMode, setActiveMode] = useState(mode);

  const [registerForm, setRegisterForm] = useState(emptyRegisterForm);
  const [loginForm, setLoginForm] = useState(emptyLoginForm);
  const [forgotEmail, setForgotEmail] = useState("");

  const [captcha, setCaptcha] = useState(generateCaptcha);
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const isRegister = activeMode === "register";
  const isLogin = activeMode === "login";
  const isForgot = activeMode === "forgot";

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;

    setError("");
    setRegisterForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;

    setError("");
    setLoginForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const goToMode = (newMode) => {
    setActiveMode(newMode);
    setError("");
    setSuccessMessage("");
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    setError("");
    setSuccessMessage("");

    const payload = normalizeRegisterForm(registerForm);
    const {
      firstName,
      lastName,
      mobile,
      email,
      username,
      password,
      confirmPassword,
    } = payload;

    if (
      !firstName ||
      !lastName ||
      !mobile ||
      !email ||
      !username ||
      !password ||
      !confirmPassword
    ) {
      setError("لطفاً همه فیلدها را کامل وارد کنید.");
      return;
    }

    if (password.length < 6) {
      setError("رمز عبور باید حداقل ۶ کاراکتر باشد.");
      return;
    }

    if (password !== confirmPassword) {
      setError("رمز عبور و تکرار رمز عبور یکسان نیستند.");
      return;
    }

    if (!captchaAnswer) {
      setError("لطفاً پاسخ کپچا را وارد کنید.");
      return;
    }

    if (Number(captchaAnswer) !== captcha.answer) {
      setError("پاسخ کپچا اشتباه است.");
      setCaptcha(generateCaptcha());
      setCaptchaAnswer("");
      return;
    }

    try {
      setLoading(true);

      const data = await registerUser(payload);

      localStorage.setItem("authToken", data.token);
      localStorage.setItem("currentUser", JSON.stringify(data.user));

      onLogin(data.user);
      onClose();
    } catch (err) {
      setError(err.message || "خطا در ثبت‌نام.");
      setCaptcha(generateCaptcha());
      setCaptchaAnswer("");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    setError("");
    setSuccessMessage("");

    const username = loginForm.username.trim();
    const { password } = loginForm;

    if (!username || !password) {
      setError("لطفاً نام کاربری و رمز عبور را وارد کنید.");
      return;
    }

    try {
      setLoading(true);

      const data = await loginUser({
        username,
        password,
      });

      localStorage.setItem("authToken", data.token);
      localStorage.setItem("currentUser", JSON.stringify(data.user));

      onLogin(data.user);
      onClose();
    } catch (err) {
      setError(err.message || "نام کاربری یا رمز عبور اشتباه است.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    setError("");
    setSuccessMessage("");

    const email = forgotEmail.trim().toLowerCase();

    if (!email) {
      setError("لطفاً آدرس ایمیل خود را وارد کنید.");
      return;
    }

    try {
      setLoading(true);

      const data = await forgotPasswordUser(email);

      setSuccessMessage(
        data.message || "لینک بازیابی رمز عبور برای شما ارسال شد."
      );
      setForgotEmail("");
    } catch (err) {
      setError(err.message || "خطا در ارسال لینک بازیابی رمز عبور.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="auth-overlay"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`auth-card ${
          isRegister ? "register-card" : isForgot ? "forgot-card" : "login-card"
        }`}
      >
        <button
          className="auth-close"
          type="button"
          onClick={onClose}
          aria-label="بستن"
        >
          ×
        </button>

        <div className="auth-icon">
          {isRegister && "📝"}
          {isLogin && "👤"}
          {isForgot && "🔐"}
        </div>

        <h2>
          {isRegister && "ثبت‌نام کاربر جدید"}
          {isLogin && "ورود به حساب کاربری"}
          {isForgot && "بازیابی رمز عبور"}
        </h2>

        <p className="auth-subtitle">
          {isRegister && "اطلاعات خود را برای ساخت حساب کاربری وارد کنید."}
          {isLogin && "نام کاربری و رمز عبور خود را وارد کنید."}
          {isForgot &&
            "ایمیل حساب کاربری خود را وارد کنید تا لینک بازیابی برای شما ارسال شود."}
        </p>

        {error && <div className="auth-error">{error}</div>}

        {successMessage && <div className="auth-success">{successMessage}</div>}

        {isRegister && (
          <form onSubmit={handleRegister} className="auth-form">
            <div className="auth-grid">
              <div className="auth-field">
                <label>نام</label>
                <input
                  type="text"
                  name="firstName"
                  value={registerForm.firstName}
                  onChange={handleRegisterChange}
                  placeholder="مثلاً علی"
                  autoComplete="given-name"
                />
              </div>

              <div className="auth-field">
                <label>نام خانوادگی</label>
                <input
                  type="text"
                  name="lastName"
                  value={registerForm.lastName}
                  onChange={handleRegisterChange}
                  placeholder="مثلاً رضایی"
                  autoComplete="family-name"
                />
              </div>

              <div className="auth-field">
                <label>شماره موبایل</label>
                <input
                  type="tel"
                  name="mobile"
                  value={registerForm.mobile}
                  onChange={handleRegisterChange}
                  placeholder="09123456789"
                  autoComplete="tel"
                  dir="ltr"
                />
              </div>

              <div className="auth-field">
                <label>آدرس ایمیل</label>
                <input
                  type="email"
                  name="email"
                  value={registerForm.email}
                  onChange={handleRegisterChange}
                  placeholder="example@email.com"
                  autoComplete="email"
                  dir="ltr"
                />
              </div>

              <div className="auth-field">
                <label>نام کاربری</label>
                <input
                  type="text"
                  name="username"
                  value={registerForm.username}
                  onChange={handleRegisterChange}
                  placeholder="username"
                  autoComplete="username"
                  dir="ltr"
                />
              </div>

              <div className="auth-field">
                <label>رمز عبور</label>
                <PasswordInput
                  name="password"
                  value={registerForm.password}
                  onChange={handleRegisterChange}
                  placeholder="حداقل ۶ کاراکتر"
                  required
                  autoComplete="new-password"
                />
              </div>

              <div className="auth-field auth-full">
                <label>تکرار رمز عبور</label>
                <PasswordInput
                  name="confirmPassword"
                  value={registerForm.confirmPassword}
                  onChange={handleRegisterChange}
                  placeholder="رمز عبور را دوباره وارد کنید"
                  required
                  autoComplete="new-password"
                />
              </div>

              <div className="auth-field auth-full">
                <label>کپچا</label>

                <div className="captcha-row">
                  <div className="captcha-question">
                    <span dir="ltr">{captcha.question}</span>
                  </div>

                  <input
                    type="number"
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    placeholder="پاسخ را وارد کنید"
                    inputMode="numeric"
                  />

                  <button
                    type="button"
                    className="captcha-refresh"
                    onClick={() => {
                      setCaptcha(generateCaptcha());
                      setCaptchaAnswer("");
                    }}
                    aria-label="تغییر کپچا"
                  >
                    ↻
                  </button>
                </div>
              </div>
            </div>

            <button className="auth-submit" type="submit" disabled={loading}>
              {loading ? "در حال ثبت‌نام..." : "ثبت‌نام"}
            </button>

            <p className="auth-switch">
              قبلاً ثبت‌نام کرده‌اید؟
              <button type="button" onClick={() => goToMode("login")}>
                ورود
              </button>
            </p>
          </form>
        )}

        {isLogin && (
          <form onSubmit={handleLogin} className="auth-form">
            <div className="auth-field">
              <label>نام کاربری</label>
              <input
                type="text"
                name="username"
                value={loginForm.username}
                onChange={handleLoginChange}
                placeholder="نام کاربری"
                autoComplete="username"
                dir="ltr"
              />
            </div>

            <div className="auth-field">
              <label>رمز عبور</label>
              <PasswordInput
                name="password"
                value={loginForm.password}
                onChange={handleLoginChange}
                placeholder="رمز عبور"
                required
                autoComplete="current-password"
              />
            </div>

            <button
              className="forgot-link"
              type="button"
              onClick={() => goToMode("forgot")}
            >
              رمز عبور را فراموش کرده‌اید؟
            </button>

            <button className="auth-submit" type="submit" disabled={loading}>
              {loading ? "در حال ورود..." : "ورود"}
            </button>

            <p className="auth-switch">
              حساب کاربری ندارید؟
              <button type="button" onClick={() => goToMode("register")}>
                ثبت‌نام
              </button>
            </p>
          </form>
        )}

        {isForgot && (
          <form onSubmit={handleForgotPassword} className="auth-form">
            <div className="auth-field">
              <label>آدرس ایمیل</label>
              <input
                type="email"
                value={forgotEmail}
                onChange={(e) => {
                  setError("");
                  setForgotEmail(e.target.value);
                }}
                placeholder="example@email.com"
                autoComplete="email"
                dir="ltr"
              />
            </div>

            <button className="auth-submit" type="submit" disabled={loading}>
              {loading ? "در حال ارسال..." : "ارسال لینک بازیابی"}
            </button>

            <p className="auth-switch">
              رمز عبور خود را به خاطر آوردید؟
              <button type="button" onClick={() => goToMode("login")}>
                بازگشت به ورود
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default AuthModal;
